'use client';

import { useTranslation } from 'react-i18next';

import { useBuyCoinsModal } from '~/components/wallet/buy-coins-modal-context';
import {
  formatCountdown,
  useFirstPurchaseOffer,
} from '~/components/wallet/use-first-purchase-offer';
import { COIN_PURCHASES_ENABLED } from '~/config/monetization.config';

/**
 * Compact navbar pill counting down the first-purchase double-coins offer
 * (24h from signup, coins doubled on the first pack). Renders nothing when
 * no offer is live; clicking opens the buy-coins modal.
 *
 * Both navbars render inside BuyCoinsModalProvider (mounted globally in
 * RootProviders), so the modal hook is always available here.
 */
export function FirstPurchasePill() {
  // Sells coin packs — meaningless while pack purchases are hidden. Gate
  // BEFORE the hooks so the offer poll (a Supabase auth read, a table read
  // and a 1s countdown interval) never runs while the flag is off.
  if (!COIN_PURCHASES_ENABLED) return null;

  return <FirstPurchasePillInner />;
}

function FirstPurchasePillInner() {
  const { t } = useTranslation('billing');
  const { openBuyCoinsModal } = useBuyCoinsModal();
  const offer = useFirstPurchaseOffer(true);

  if (!offer) {
    return null;
  }

  const countdown = formatCountdown(offer.remainingMs);
  // HH:MM only — tight screens drop the seconds and the label.
  const shortCountdown = countdown.slice(0, 5);

  return (
    <button
      type="button"
      onClick={openBuyCoinsModal}
      title={t('firstPurchasePill.label')}
      className="flex flex-shrink-0 items-center gap-1 rounded-full border border-amber-400/50 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold whitespace-nowrap text-amber-300 transition-all hover:border-amber-300 hover:bg-amber-500/20 hover:shadow-[0_0_15px_rgba(251,191,36,0.25)] sm:gap-1.5 sm:px-2.5 sm:text-[11px]"
      data-test="first-purchase-pill"
    >
      <span aria-hidden>🎁</span>
      <span className="hidden sm:inline">{t('firstPurchasePill.label')}</span>
      <span className="hidden font-mono tabular-nums sm:inline">
        {countdown}
      </span>
      <span className="font-mono tabular-nums sm:hidden">{shortCountdown}</span>
    </button>
  );
}
